/**
 * Registry of named motion sequences for show scripting.
 * Scripts reference these by name (e.g. "wave", "bow", "hit") so we don't depend on the LLM at playback.
 */

import type { MotionSequence } from '../llm/types'

export const NAMED_MOTIONS: Record<string, MotionSequence> = {
  wave: {
    steps: [
      { startTime: 0, duration: 0.5, rotations: { rightArm: { x: -0.5, y: 0.3 } } },
      { startTime: 0.5, duration: 0.3, rotations: { rightArm: { x: -0.8, y: 0.5 } } },
      { startTime: 0.8, duration: 0.3, rotations: { rightArm: { x: -0.5, y: 0.3 } } },
      { startTime: 1.1, duration: 0.2, rotations: { rightArm: { x: 0, y: 0 } } },
    ],
    totalDuration: 1.3,
  },
  bow: {
    steps: [
      { startTime: 0, duration: 0.5, rotations: { torso: { x: 0.3 }, head: { x: 0.2 } } },
      { startTime: 0.5, duration: 0.5, rotations: { torso: { x: 0 }, head: { x: 0 } } },
    ],
    totalDuration: 1.0,
  },
  nod: {
    steps: [
      { startTime: 0, duration: 0.25, rotations: { head: { x: 0.2 } } },
      { startTime: 0.25, duration: 0.25, rotations: { head: { x: 0 } } },
      { startTime: 0.5, duration: 0.25, rotations: { head: { x: 0.2 } } },
      { startTime: 0.75, duration: 0.25, rotations: { head: { x: 0 } } },
    ],
    totalDuration: 1.0,
  },
  shakeHead: {
    steps: [
      { startTime: 0, duration: 0.2, rotations: { head: { y: 0.3 } } },
      { startTime: 0.2, duration: 0.2, rotations: { head: { y: -0.3 } } },
      { startTime: 0.4, duration: 0.2, rotations: { head: { y: 0.3 } } },
      { startTime: 0.6, duration: 0.2, rotations: { head: { y: 0 } } },
    ],
    totalDuration: 0.8,
  },
  /** Punch-style hit with stick (one arm forward) */
  hit: {
    steps: [
      { startTime: 0, duration: 0.15, rotations: { rightArm: { x: -0.9, y: 0.5 } } },
      { startTime: 0.15, duration: 0.2, rotations: { rightArm: { x: -0.2, y: 0.8 } } },
      { startTime: 0.35, duration: 0.3, rotations: { rightArm: { x: -0.5, y: 0.3 } } },
    ],
    totalDuration: 0.65,
  },
  /** Judy scolding / pointing */
  point: {
    steps: [
      { startTime: 0, duration: 0.3, rotations: { rightArm: { x: -0.4, y: -0.5 }, head: { x: -0.2 } } },
      { startTime: 0.3, duration: 0.5, rotations: { rightArm: { x: -0.4, y: -0.5 } } },
      { startTime: 0.8, duration: 0.3, rotations: { rightArm: { x: 0, y: 0 }, head: { x: 0 } } },
    ],
    totalDuration: 1.1,
  },
  /** Idle sway */
  idle: {
    steps: [
      { startTime: 0, duration: 1.5, rotations: { torso: { y: 0.02 }, head: { y: 0.01 } } },
      { startTime: 1.5, duration: 1.5, rotations: { torso: { y: -0.02 }, head: { y: -0.01 } } },
    ],
    totalDuration: 3.0,
  },
  /** Enter from side (lean then settle) */
  enter: {
    steps: [
      { startTime: 0, duration: 0.5, rotations: { torso: { y: 0.3 }, head: { y: 0.2 } } },
      { startTime: 0.5, duration: 0.5, rotations: { torso: { y: 0 }, head: { y: 0 } } },
    ],
    totalDuration: 1.0,
  },
}

export function getMotion(name: string): MotionSequence | null {
  return NAMED_MOTIONS[name] ?? null
}
